import React, { useEffect, useState } from 'react';
import { apiService } from '../services/api.service';

type Tab = 'base' | 'iva' | 'legal' | 'pagares' | 'seguros' | 'afectaciones' | 'facturas';

const BUREAU_PROVIDER_LABELS: Record<string, string> = {
  VERAZ: 'Veraz',
  VERAZ_COLLECTION: 'Veraz Collection',
  RIESGO_ONLINE: 'Riesgo OnLine',
  PYP: 'PyP',
  APICC: 'Apicc',
  PROT_COM: 'Prot Com',
  OTHER: 'Otro',
};

const TABS: { id: Tab; label: string }[] = [
  { id: 'base', label: 'Base imponible' },
  { id: 'iva', label: 'Libro IVA' },
  { id: 'legal', label: 'Razones sociales' },
  { id: 'facturas', label: 'Factura electrónica' },
  { id: 'pagares', label: 'Pagarés' },
  { id: 'seguros', label: 'Seguros' },
  { id: 'afectaciones', label: 'Afectaciones buró' },
];

export function FiscalLegal() {
  const now = new Date();
  const [tab, setTab] = useState<Tab>('base');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [taxSummary, setTaxSummary] = useState<any[]>([]);
  const [taxDetail, setTaxDetail] = useState<any | null>(null);
  const [selectedEntityId, setSelectedEntityId] = useState<string>('');
  const [confirmNotes, setConfirmNotes] = useState('');
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [ivaSummary, setIvaSummary] = useState<any | null>(null);
  const [ivaEntries, setIvaEntries] = useState<any[]>([]);
  const [legalEntities, setLegalEntities] = useState<any[]>([]);
  const [promissoryNotes, setPromissoryNotes] = useState<any[]>([]);
  const [loans, setLoans] = useState<any[]>([]);
  const [pagareForm, setPagareForm] = useState({ loanId: '', noteNumber: '', dueDate: '', notes: '' });
  const [pagareBusy, setPagareBusy] = useState(false);
  const [insurances, setInsurances] = useState<any[]>([]);
  const [affectations, setAffectations] = useState<any[]>([]);
  const [pypStatus, setPypStatus] = useState<any | null>(null);
  const [apiccStatus, setApiccStatus] = useState<any | null>(null);
  const [electronicInvoices, setElectronicInvoices] = useState<any[]>([]);
  const [invoiceQueue, setInvoiceQueue] = useState<any[]>([]);
  const [verazStatus, setVerazStatus] = useState<any | null>(null);
  const [afipStatus, setAfipStatus] = useState<any | null>(null);
  const [afipConfig, setAfipConfig] = useState<any | null>(null);
  const [queueBusy, setQueueBusy] = useState(false);

  const [legalForm, setLegalForm] = useState({
    organizationId: '',
    code: '',
    legalName: '',
    taxId: '',
    ivaCondition: 'RESP_INSCRIPTO',
  });
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [adjustForm, setAdjustForm] = useState({ legalEntityId: '', adjustmentAmount: '', reason: '' });
  const [afipForm, setAfipForm] = useState({
    legalEntityId: '',
    cuit: '',
    pointOfSale: '1',
    integrationMode: 'STUB',
    environment: 'HOMOLOGATION',
    defaultInvoiceType: 'FACTURA_B',
    certificateRef: '',
    billInterest: true,
    billPenalty: true,
    billPrincipal: false,
  });
  const [certFile, setCertFile] = useState<File | null>(null);
  const [keyFile, setKeyFile] = useState<File | null>(null);
  const [p12File, setP12File] = useState<File | null>(null);
  const [p12Password, setP12Password] = useState('');
  const [certUploading, setCertUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'base') {
        const summary = await apiService.getTaxableBaseSummary(year, month);
        setTaxSummary(summary);
        const detail = await apiService.getTaxableBaseDetail(year, month);
        setTaxDetail(detail);
        const defaultEntity = detail?.entities?.find((e: any) => e.legalEntityId === selectedEntityId) ?? detail?.entities?.[0];
        if (defaultEntity && !selectedEntityId) setSelectedEntityId(defaultEntity.legalEntityId);
      } else if (tab === 'iva') {
        const [summary, entries, entities] = await Promise.all([
          apiService.getIvaBookSummary(year, month),
          apiService.listIvaBookEntries(year, month),
          apiService.listLegalEntities(),
        ]);
        setIvaSummary(summary);
        setIvaEntries(entries);
        setLegalEntities(entities);
      } else if (tab === 'legal') {
        const [entities, orgs] = await Promise.all([
          apiService.listLegalEntities(),
          apiService.listOrganizations(),
        ]);
        setLegalEntities(entities);
        setOrganizations(orgs ?? []);
        if (!legalForm.organizationId && orgs?.[0]?.id) {
          setLegalForm((f) => ({ ...f, organizationId: orgs[0].id }));
        }
      } else if (tab === 'pagares') {
        const [notes, loanList] = await Promise.all([
          apiService.listPromissoryNotes(),
          apiService.getLoans(),
        ]);
        setPromissoryNotes(notes);
        setLoans(loanList);
      } else if (tab === 'seguros') {
        setInsurances(await apiService.listLoanInsurances());
      } else if (tab === 'afectaciones') {
        const [rows, pyp, apicc] = await Promise.all([
          apiService.listBureauAffectations(),
          apiService.getPypStatus(),
          apiService.getApiccStatus(),
        ]);
        setAffectations(rows);
        setPypStatus(pyp);
        setApiccStatus(apicc);
      } else if (tab === 'facturas') {
        const [entities, invoices, queue, veraz, afip] = await Promise.all([
          apiService.listLegalEntities(),
          apiService.listElectronicInvoices(),
          apiService.listElectronicInvoiceQueue(),
          apiService.getVerazStatus(),
          apiService.getAfipIntegrationStatus(),
        ]);
        setLegalEntities(entities);
        setElectronicInvoices(invoices);
        setInvoiceQueue(queue);
        setVerazStatus(veraz);
        setAfipStatus(afip);
        const defaultEntity = entities.find((e: any) => e.isDefault) ?? entities[0];
        if (defaultEntity) {
          const cfg = await apiService.getAfipConfig(defaultEntity.id);
          setAfipConfig(cfg);
          setAfipForm({
            legalEntityId: defaultEntity.id,
            cuit: cfg.cuit ?? '',
            pointOfSale: String(cfg.pointOfSale ?? 1),
            integrationMode: cfg.integrationMode ?? 'STUB',
            environment: cfg.environment ?? 'HOMOLOGATION',
            defaultInvoiceType: cfg.defaultInvoiceType ?? 'FACTURA_B',
            certificateRef: cfg.certificateRef ?? '',
            billInterest: cfg.billInterest ?? true,
            billPenalty: cfg.billPenalty ?? true,
            billPrincipal: cfg.billPrincipal ?? false,
          });
        }
      }
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [tab, year, month]);

  const processDevengado = async () => {
    setLoading(true);
    try {
      await apiService.processDevengado(year, month);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedEntity = taxDetail?.entities?.find((e: any) => e.legalEntityId === selectedEntityId);

  const confirmPeriod = async () => {
    if (!selectedEntityId) return;
    if (!confirm('¿Confirmar (cerrar) el período? Esto marca el mes como cerrado para auditoría.')) return;
    setConfirmBusy(true);
    setError(null);
    try {
      await apiService.confirmIvaPeriod({
        legalEntityId: selectedEntityId,
        year,
        month,
        notes: confirmNotes.trim() || undefined,
      });
      setConfirmNotes('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setConfirmBusy(false);
    }
  };

  const downloadBaseXlsx = async () => {
    if (!selectedEntityId) return;
    try {
      await apiService.downloadTaxableBaseDetailXlsx({ year, month, legalEntityId: selectedEntityId });
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const createLegalEntity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!legalForm.organizationId) {
      setError('Seleccioná la organización (grupo)');
      return;
    }
    try {
      await apiService.createLegalEntity({
        organizationId: legalForm.organizationId,
        code: legalForm.code,
        legalName: legalForm.legalName,
        taxId: legalForm.taxId || undefined,
        ivaCondition: legalForm.ivaCondition,
        isDefault: false,
      });
      setLegalForm({
        organizationId: legalForm.organizationId,
        code: '',
        legalName: '',
        taxId: '',
        ivaCondition: 'RESP_INSCRIPTO',
      });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const createAdjustment = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiService.createIvaAdjustment({
        legalEntityId: adjustForm.legalEntityId,
        periodYear: year,
        periodMonth: month,
        adjustmentAmount: parseFloat(adjustForm.adjustmentAmount),
        reason: adjustForm.reason,
      });
      setAdjustForm({ legalEntityId: '', adjustmentAmount: '', reason: '' });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const reportAffectation = async (id: string) => {
    try {
      await apiService.reportBureauAffectation(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const createPagare = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pagareForm.loanId) return;
    setPagareBusy(true);
    setError(null);
    try {
      await apiService.createPromissoryNote({
        loanId: pagareForm.loanId,
        noteNumber: pagareForm.noteNumber || undefined,
        dueDate: pagareForm.dueDate || undefined,
        notes: pagareForm.notes || undefined,
      });
      setPagareForm({ loanId: '', noteNumber: '', dueDate: '', notes: '' });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setPagareBusy(false);
    }
  };

  const updatePagareStatus = async (id: string, status: string) => {
    try {
      await apiService.updatePromissoryNoteStatus(id, status);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const PAGARE_STATUSES = ['ACTIVE', 'DUE_SOON', 'MISSING', 'RECOVERED', 'CANCELLED'];

  const uploadAfipCert = async () => {
    if (!afipForm.legalEntityId) return;
    setCertUploading(true);
    setError(null);
    try {
      const cfg = await apiService.uploadAfipCertificate(
        afipForm.legalEntityId,
        { cert: certFile ?? undefined, key: keyFile ?? undefined, p12: p12File ?? undefined },
        p12Password || undefined,
      );
      setAfipConfig(cfg);
      setAfipForm((f) => ({ ...f, certificateRef: cfg.certificateRef ?? f.certificateRef }));
      setCertFile(null);
      setKeyFile(null);
      setP12File(null);
      setP12Password('');
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setCertUploading(false);
    }
  };

  const saveAfipConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiService.updateAfipConfig(afipForm.legalEntityId, {
        cuit: afipForm.cuit,
        pointOfSale: parseInt(afipForm.pointOfSale, 10),
        integrationMode: afipForm.integrationMode,
        environment: afipForm.environment,
        defaultInvoiceType: afipForm.defaultInvoiceType,
        certificateRef: afipForm.certificateRef || undefined,
        billInterest: afipForm.billInterest,
        billPenalty: afipForm.billPenalty,
        billPrincipal: afipForm.billPrincipal,
      });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const loadAfipConfigForEntity = async (legalEntityId: string) => {
    try {
      const cfg = await apiService.getAfipConfig(legalEntityId);
      setAfipConfig(cfg);
      setAfipForm({
        legalEntityId,
        cuit: cfg.cuit ?? '',
        pointOfSale: String(cfg.pointOfSale ?? 1),
        integrationMode: cfg.integrationMode ?? 'STUB',
        environment: cfg.environment ?? 'HOMOLOGATION',
        defaultInvoiceType: cfg.defaultInvoiceType ?? 'FACTURA_B',
        certificateRef: cfg.certificateRef ?? '',
        billInterest: cfg.billInterest ?? true,
        billPenalty: cfg.billPenalty ?? true,
        billPrincipal: cfg.billPrincipal ?? false,
      });
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const processInvoiceQueue = async () => {
    setQueueBusy(true);
    try {
      await apiService.processElectronicInvoiceQueue({
        legalEntityId: afipForm.legalEntityId || undefined,
        limit: 50,
      });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setQueueBusy(false);
    }
  };

  const retryInvoice = async (id: string) => {
    try {
      await apiService.retryElectronicInvoice(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const downloadInvoicePdf = async (inv: { id: string; fullNumber: string }) => {
    try {
      await apiService.downloadElectronicInvoicePdf(
        inv.id,
        `comprobante-${inv.fullNumber.replace('-', '_')}.pdf`,
      );
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Fiscal y legal</h1>
        <p className="text-sm text-gray-500 mt-1">Base imponible, IVA, factura electrónica AFIP, pagarés, seguros y buró Veraz</p>
      </div>

      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-sm rounded-md ${tab === t.id ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {(tab === 'base' || tab === 'iva') && (
        <div className="flex gap-4 items-end">
          <div>
            <label className="text-sm text-gray-600">Año</label>
            <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className="ml-2 px-2 py-1 border rounded w-24" />
          </div>
          <div>
            <label className="text-sm text-gray-600">Mes</label>
            <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} className="ml-2 px-2 py-1 border rounded">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          {tab === 'base' && (
            <>
              <button onClick={processDevengado} disabled={loading} className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
                Procesar devengado
              </button>
              <button onClick={downloadBaseXlsx} disabled={loading || !selectedEntityId} className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                Descargar Excel
              </button>
            </>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Cargando…</p>
      ) : (
        <>
          {tab === 'base' && (
            <div className="space-y-4">
              {taxDetail?.entities?.length > 0 && (
                <div className="bg-white border rounded-lg p-4 space-y-3">
                  <div className="flex flex-wrap gap-2 items-end">
                    <div>
                      <label className="text-xs text-gray-500">Razón social</label>
                      <select
                        value={selectedEntityId}
                        onChange={(e) => setSelectedEntityId(e.target.value)}
                        className="ml-2 px-2 py-1 border rounded text-sm"
                      >
                        {taxDetail.entities.map((le: any) => (
                          <option key={le.legalEntityId} value={le.legalEntityId}>
                            {le.legalEntityName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="ml-auto text-sm">
                      {selectedEntity?.confirmed ? (
                        <span className="px-2 py-1 rounded bg-green-50 border border-green-200 text-green-800 text-xs">
                          Período confirmado {selectedEntity.confirmedAt ? `(${String(selectedEntity.confirmedAt).slice(0, 10)})` : ''}
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                          Período abierto
                        </span>
                      )}
                    </div>
                  </div>

                  {!selectedEntity?.confirmed && (
                    <div className="flex flex-wrap gap-2 items-end">
                      <input
                        value={confirmNotes}
                        onChange={(e) => setConfirmNotes(e.target.value)}
                        placeholder="Notas cierre (opcional)"
                        className="px-2 py-1 border rounded text-sm flex-1 min-w-[220px]"
                      />
                      <button
                        onClick={confirmPeriod}
                        disabled={confirmBusy || loading}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {confirmBusy ? 'Confirmando…' : 'Confirmar período'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {taxSummary.map((s) => (
                <div key={s.legalEntityId} className="bg-white border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900">{s.legalEntityName}</h3>
                  <div className="grid md:grid-cols-3 gap-4 mt-3 text-sm">
                    <div className="p-3 bg-blue-50 rounded">
                      <p className="text-gray-500">Facturado</p>
                      <p className="text-lg font-bold">${s.facturado.base.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">IVA ${s.facturado.iva.toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded">
                      <p className="text-gray-500">Devengado</p>
                      <p className="text-lg font-bold">${s.devengado.base.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">IVA ${s.devengado.iva.toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded">
                      <p className="text-gray-500">Diferencia (fact. − deveng.)</p>
                      <p className="text-lg font-bold">${s.variance.base.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">IVA ${s.variance.iva.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              ))}
              {taxSummary.length === 0 && <p className="text-gray-500">Sin movimientos en el período.</p>}

              {taxDetail && selectedEntityId && (
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { title: 'FACTURADO', rows: taxDetail.facturado?.filter((r: any) => r.legalEntityId === selectedEntityId) ?? [] },
                    { title: 'DEVENGADO', rows: taxDetail.devengado?.filter((r: any) => r.legalEntityId === selectedEntityId) ?? [] },
                  ].map((block) => (
                    <div key={block.title} className="bg-white border rounded-lg overflow-hidden">
                      <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900">{block.title}</h3>
                        <span className="text-xs text-gray-500">{block.rows.length} fila(s)</span>
                      </div>
                      <div className="overflow-auto">
                        <table className="min-w-full text-xs">
                          <thead className="bg-white sticky top-0">
                            <tr className="border-b">
                              <th className="px-2 py-2 text-left">Fecha</th>
                              <th className="px-2 py-2 text-left">Código</th>
                              <th className="px-2 py-2 text-left">Descripción</th>
                              <th className="px-2 py-2 text-left">Préstamo</th>
                              <th className="px-2 py-2 text-right">DP</th>
                              <th className="px-2 py-2 text-right">DR</th>
                              <th className="px-2 py-2 text-right">PP</th>
                              <th className="px-2 py-2 text-right">PR</th>
                              <th className="px-2 py-2 text-right">A</th>
                              <th className="px-2 py-2 text-right">Base</th>
                              <th className="px-2 py-2 text-right">IVA</th>
                            </tr>
                          </thead>
                          <tbody>
                            {block.rows.map((r: any) => (
                              <tr key={r.id} className="border-t">
                                <td className="px-2 py-2 whitespace-nowrap">{String(r.entryDate).slice(0, 10)}</td>
                                <td className="px-2 py-2 font-mono">{r.legacyCode}</td>
                                <td className="px-2 py-2">{r.description ?? '—'}</td>
                                <td className="px-2 py-2 font-mono">{r.loanNumber ?? '—'}</td>
                                <td className="px-2 py-2 text-right">{r.DP ? `$${Number(r.DP).toLocaleString()}` : ''}</td>
                                <td className="px-2 py-2 text-right">{r.DR ? `$${Number(r.DR).toLocaleString()}` : ''}</td>
                                <td className="px-2 py-2 text-right">{r.PP ? `$${Number(r.PP).toLocaleString()}` : ''}</td>
                                <td className="px-2 py-2 text-right">{r.PR ? `$${Number(r.PR).toLocaleString()}` : ''}</td>
                                <td className="px-2 py-2 text-right">{r.A ? `$${Number(r.A).toLocaleString()}` : ''}</td>
                                <td className="px-2 py-2 text-right font-semibold">{`$${Number(r.baseAmount).toLocaleString()}`}</td>
                                <td className="px-2 py-2 text-right">{`$${Number(r.ivaAmount).toLocaleString()}`}</td>
                              </tr>
                            ))}
                            {block.rows.length === 0 && (
                              <tr>
                                <td className="px-2 py-6 text-center text-gray-500" colSpan={11}>
                                  Sin filas.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'iva' && (
            <div className="space-y-4">
              {ivaSummary && (
                <div className="grid md:grid-cols-4 gap-3">
                  <div className="p-3 bg-white border rounded"><p className="text-xs text-gray-500">IVA ventas</p><p className="text-xl font-bold">${ivaSummary.ventas.iva.toLocaleString()}</p></div>
                  <div className="p-3 bg-white border rounded"><p className="text-xs text-gray-500">IVA compras</p><p className="text-xl font-bold">${ivaSummary.compras.iva.toLocaleString()}</p></div>
                  <div className="p-3 bg-white border rounded"><p className="text-xs text-gray-500">Ajustes</p><p className="text-xl font-bold">${ivaSummary.adjustments.total.toLocaleString()}</p></div>
                  <div className="p-3 bg-green-50 border border-green-200 rounded"><p className="text-xs text-gray-500">Saldo IVA</p><p className="text-xl font-bold text-green-800">${ivaSummary.ivaSaldo.toLocaleString()}</p></div>
                </div>
              )}
              <form onSubmit={createAdjustment} className="flex flex-wrap gap-2 items-end bg-white border p-4 rounded-lg">
                <select required value={adjustForm.legalEntityId} onChange={(e) => setAdjustForm({ ...adjustForm, legalEntityId: e.target.value })} className="px-2 py-1 border rounded text-sm">
                  <option value="">Razón social</option>
                  {legalEntities.map((le) => <option key={le.id} value={le.id}>{le.legalName}</option>)}
                </select>
                <input required type="number" step="0.01" placeholder="Monto ajuste" value={adjustForm.adjustmentAmount} onChange={(e) => setAdjustForm({ ...adjustForm, adjustmentAmount: e.target.value })} className="px-2 py-1 border rounded text-sm w-32" />
                <input required placeholder="Motivo" value={adjustForm.reason} onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })} className="px-2 py-1 border rounded text-sm flex-1 min-w-[200px]" />
                <button type="submit" className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded">+ Ajuste IVA</button>
              </form>
              <table className="min-w-full text-sm bg-white border rounded-lg overflow-hidden">
                <thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left">Fecha</th><th className="px-3 py-2 text-left">Tipo</th><th className="px-3 py-2 text-right">Neto</th><th className="px-3 py-2 text-right">IVA</th><th className="px-3 py-2 text-right">Total</th></tr></thead>
                <tbody>
                  {ivaEntries.map((e) => (
                    <tr key={e.id} className="border-t">
                      <td className="px-3 py-2">{new Date(e.entryDate).toLocaleDateString()}</td>
                      <td className="px-3 py-2">{e.bookType}</td>
                      <td className="px-3 py-2 text-right">${e.netAmount?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right">${e.ivaAmount?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right">${e.totalAmount?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'legal' && (
            <div className="space-y-4">
              <form onSubmit={createLegalEntity} className="grid md:grid-cols-5 gap-3 bg-white border p-4 rounded-lg">
                <select
                  required
                  value={legalForm.organizationId}
                  onChange={(e) => setLegalForm({ ...legalForm, organizationId: e.target.value })}
                  className="px-2 py-1 border rounded"
                >
                  <option value="">Grupo…</option>
                  {organizations.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                <input required placeholder="Código" value={legalForm.code} onChange={(e) => setLegalForm({ ...legalForm, code: e.target.value })} className="px-2 py-1 border rounded" />
                <input required placeholder="Razón social" value={legalForm.legalName} onChange={(e) => setLegalForm({ ...legalForm, legalName: e.target.value })} className="px-2 py-1 border rounded" />
                <input placeholder="CUIT" value={legalForm.taxId} onChange={(e) => setLegalForm({ ...legalForm, taxId: e.target.value })} className="px-2 py-1 border rounded" />
                <button type="submit" className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm">Agregar</button>
              </form>
              {legalEntities.map((le) => (
                <div key={le.id} className="bg-white border rounded-lg p-4">
                  <p className="font-semibold">{le.legalName} {le.isDefault && <span className="text-xs text-blue-600">(default)</span>}</p>
                  <p className="text-sm text-gray-500 font-mono">{le.code} · CUIT {le.taxId ?? '—'} · {le.ivaCondition}</p>
                  {le.branches?.length > 0 && (
                    <p className="text-xs text-gray-400 mt-1">Sucursales: {le.branches.map((b: any) => b.name).join(', ')}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === 'pagares' && (
            <div className="space-y-4">
              <form onSubmit={createPagare} className="grid md:grid-cols-5 gap-2 bg-white border p-4 rounded-lg">
                <select
                  required
                  value={pagareForm.loanId}
                  onChange={(e) => setPagareForm({ ...pagareForm, loanId: e.target.value })}
                  className="px-2 py-1 border rounded text-sm"
                >
                  <option value="">Préstamo…</option>
                  {loans.map((l: any) => (
                    <option key={l.id} value={l.id}>
                      {l.loanNumber ?? l.id.slice(0, 8)} · ${l.amount?.toLocaleString?.() ?? l.amount}
                    </option>
                  ))}
                </select>
                <input
                  placeholder="Nº pagaré"
                  value={pagareForm.noteNumber}
                  onChange={(e) => setPagareForm({ ...pagareForm, noteNumber: e.target.value })}
                  className="px-2 py-1 border rounded text-sm"
                />
                <input
                  type="date"
                  value={pagareForm.dueDate}
                  onChange={(e) => setPagareForm({ ...pagareForm, dueDate: e.target.value })}
                  className="px-2 py-1 border rounded text-sm"
                />
                <input
                  placeholder="Notas"
                  value={pagareForm.notes}
                  onChange={(e) => setPagareForm({ ...pagareForm, notes: e.target.value })}
                  className="px-2 py-1 border rounded text-sm"
                />
                <button
                  type="submit"
                  disabled={pagareBusy}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm disabled:opacity-50"
                >
                  + Pagaré
                </button>
              </form>
              <table className="min-w-full text-sm bg-white border rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2">Préstamo</th>
                    <th className="px-3 py-2">Cliente</th>
                    <th className="px-3 py-2">Nº pagaré</th>
                    <th className="px-3 py-2">Estado</th>
                    <th className="px-3 py-2">Vencimiento</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {promissoryNotes.map((n) => (
                    <tr key={n.id} className="border-t">
                      <td className="px-3 py-2">{n.loan?.loanNumber ?? n.loanId?.slice(0, 8)}</td>
                      <td className="px-3 py-2">{n.loan?.customer?.firstName} {n.loan?.customer?.lastName}</td>
                      <td className="px-3 py-2">{n.noteNumber ?? '—'}</td>
                      <td className="px-3 py-2">{n.status}</td>
                      <td className="px-3 py-2">{n.dueDate ? new Date(n.dueDate).toLocaleDateString() : '—'}</td>
                      <td className="px-3 py-2">
                        <select
                          className="text-xs border rounded px-1 py-0.5"
                          value={n.status}
                          onChange={(e) => updatePagareStatus(n.id, e.target.value)}
                        >
                          {PAGARE_STATUSES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'seguros' && (
            <table className="min-w-full text-sm bg-white border rounded-lg">
              <thead className="bg-gray-50"><tr><th className="px-3 py-2">Préstamo</th><th className="px-3 py-2">Aseguradora</th><th className="px-3 py-2">Póliza</th><th className="px-3 py-2">Prima</th><th className="px-3 py-2">Estado</th></tr></thead>
              <tbody>
                {insurances.map((i) => (
                  <tr key={i.id} className="border-t">
                    <td className="px-3 py-2">{i.loan?.loanNumber}</td>
                    <td className="px-3 py-2">{i.providerName}</td>
                    <td className="px-3 py-2">{i.policyNumber ?? '—'}</td>
                    <td className="px-3 py-2">{i.premium ? `$${i.premium.toLocaleString()}` : '—'}</td>
                    <td className="px-3 py-2">{i.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab === 'afectaciones' && (
            <div className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div className="bg-white border rounded-lg p-3 text-sm">
                  <h3 className="font-semibold text-gray-900">PyP</h3>
                  {pypStatus && (
                    <p className="text-gray-600 mt-1">
                      Modo: <span className="font-mono">{pypStatus.mode}</span>
                      {pypStatus.configured ? ' · configurado' : ' · pendiente credenciales'}
                    </p>
                  )}
                </div>
                <div className="bg-white border rounded-lg p-3 text-sm">
                  <h3 className="font-semibold text-gray-900">Apicc</h3>
                  {apiccStatus && (
                    <p className="text-gray-600 mt-1">
                      Modo: <span className="font-mono">{apiccStatus.mode}</span>
                      {apiccStatus.configured ? ' · configurado' : ' · pendiente credenciales'}
                    </p>
                  )}
                </div>
              </div>
              <button onClick={() => apiService.processBureauAffectationsAuto().then(load)} className="text-sm text-blue-600 hover:underline">
                Ejecutar detección automática
              </button>
              <table className="min-w-full text-sm bg-white border rounded-lg">
                <thead className="bg-gray-50"><tr><th className="px-3 py-2">Documento</th><th className="px-3 py-2">Proveedor</th><th className="px-3 py-2">Préstamo</th><th className="px-3 py-2">Días mora</th><th className="px-3 py-2">Monto</th><th className="px-3 py-2">Estado</th><th className="px-3 py-2"></th></tr></thead>
                <tbody>
                  {affectations.map((a) => (
                    <tr key={a.id} className="border-t">
                      <td className="px-3 py-2">{a.documentNumber}</td>
                      <td className="px-3 py-2">{BUREAU_PROVIDER_LABELS[a.provider] ?? a.provider}</td>
                      <td className="px-3 py-2">{a.loan?.loanNumber ?? '—'}</td>
                      <td className="px-3 py-2">{a.overdueDays ?? '—'}</td>
                      <td className="px-3 py-2">{a.overdueAmount ? `$${a.overdueAmount.toLocaleString()}` : '—'}</td>
                      <td className="px-3 py-2">{a.status}</td>
                      <td className="px-3 py-2">
                        {a.status === 'PENDING' && (
                          <button onClick={() => reportAffectation(a.id)} className="text-green-600 hover:underline text-xs">
                            Reportar a {BUREAU_PROVIDER_LABELS[a.provider] ?? a.provider}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'facturas' && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900">Veraz</h3>
                  {verazStatus && (
                    <p className="text-sm text-gray-600 mt-2">
                      Modo: <span className="font-mono">{verazStatus.mode}</span>
                      {verazStatus.configured ? ' · configurado' : ' · pendiente credenciales'}
                    </p>
                  )}
                </div>
                <div className="bg-white border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900">AFIP</h3>
                  {afipStatus && (
                    <p className="text-sm text-gray-600 mt-2">
                      Env fallback: <span className="font-mono">{afipStatus.mode}</span>
                      {afipConfig && (
                        <>
                          {' '}· LE: <span className="font-mono">{afipConfig.integrationMode ?? 'STUB'}</span>
                          {' / '}{afipConfig.environment}
                        </>
                      )}
                      {afipConfig?.integrationMode === 'WSFE' && ' · WSAA+WSFE'}
                      {afipConfig?.certificateRef || afipStatus.certificateConfigured
                        ? ' · certificado OK'
                        : ' · sin certificado'}
                    </p>
                  )}
                </div>
              </div>

              <form onSubmit={saveAfipConfig} className="grid md:grid-cols-6 gap-3 bg-white border p-4 rounded-lg items-end">
                <div>
                  <label className="text-xs text-gray-500">Razón social</label>
                  <select
                    value={afipForm.legalEntityId}
                    onChange={(e) => loadAfipConfigForEntity(e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm mt-1"
                  >
                    {legalEntities.map((le) => <option key={le.id} value={le.id}>{le.legalName}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500">CUIT</label>
                  <input value={afipForm.cuit} onChange={(e) => setAfipForm({ ...afipForm, cuit: e.target.value })} className="w-full px-2 py-1 border rounded text-sm mt-1" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Punto de venta</label>
                  <input value={afipForm.pointOfSale} onChange={(e) => setAfipForm({ ...afipForm, pointOfSale: e.target.value })} className="w-full px-2 py-1 border rounded text-sm mt-1" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Modo</label>
                  <select value={afipForm.integrationMode} onChange={(e) => setAfipForm({ ...afipForm, integrationMode: e.target.value })} className="w-full px-2 py-1 border rounded text-sm mt-1">
                    <option value="STUB">Stub (simulado)</option>
                    <option value="WSFE">WSFE (ARCA)</option>
                    <option value="GATEWAY">Gateway</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Ambiente</label>
                  <select value={afipForm.environment} onChange={(e) => setAfipForm({ ...afipForm, environment: e.target.value })} className="w-full px-2 py-1 border rounded text-sm mt-1">
                    <option value="HOMOLOGATION">Homologación</option>
                    <option value="PRODUCTION">Producción</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Tipo comprobante</label>
                  <select value={afipForm.defaultInvoiceType} onChange={(e) => setAfipForm({ ...afipForm, defaultInvoiceType: e.target.value })} className="w-full px-2 py-1 border rounded text-sm mt-1">
                    <option value="FACTURA_B">Factura B</option>
                    <option value="FACTURA_C">Factura C</option>
                    <option value="FACTURA_A">Factura A</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-gray-500">Certificado (path interno)</label>
                  <input
                    value={afipForm.certificateRef}
                    readOnly
                    placeholder="Se completa al subir el archivo"
                    className="w-full px-2 py-1 border rounded text-sm mt-1 font-mono bg-gray-50"
                    disabled={afipForm.integrationMode === 'STUB'}
                  />
                </div>
                <div className="md:col-span-6 grid md:grid-cols-4 gap-3 items-end border-t pt-3">
                  <div>
                    <label className="text-xs text-gray-500">Certificado (.crt / .pem)</label>
                    <input
                      type="file"
                      accept=".crt,.pem,.cer"
                      className="w-full text-sm mt-1"
                      disabled={afipForm.integrationMode === 'STUB' || certUploading}
                      onChange={(e) => setCertFile(e.target.files?.[0] ?? null)}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Clave privada (.key)</label>
                    <input
                      type="file"
                      accept=".key,.pem"
                      className="w-full text-sm mt-1"
                      disabled={afipForm.integrationMode === 'STUB' || certUploading}
                      onChange={(e) => setKeyFile(e.target.files?.[0] ?? null)}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">O P12/PFX</label>
                    <input
                      type="file"
                      accept=".p12,.pfx"
                      className="w-full text-sm mt-1"
                      disabled={afipForm.integrationMode === 'STUB' || certUploading}
                      onChange={(e) => setP12File(e.target.files?.[0] ?? null)}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Password P12 (si aplica)</label>
                    <input
                      type="password"
                      value={p12Password}
                      onChange={(e) => setP12Password(e.target.value)}
                      className="w-full px-2 py-1 border rounded text-sm mt-1"
                      disabled={afipForm.integrationMode === 'STUB' || certUploading}
                    />
                  </div>
                  <div className="md:col-span-4 flex gap-2">
                    <button
                      type="button"
                      disabled={afipForm.integrationMode === 'STUB' || certUploading || (!certFile && !keyFile && !p12File)}
                      onClick={uploadAfipCert}
                      className="px-3 py-1.5 bg-emerald-700 text-white rounded text-sm disabled:opacity-50"
                    >
                      {certUploading ? 'Subiendo…' : 'Subir certificado'}
                    </button>
                    <p className="text-xs text-gray-500 self-center">
                      No hace falta acceso al servidor: el archivo queda en el volumen del API.
                    </p>
                  </div>
                </div>
                <div className="md:col-span-3 flex flex-wrap gap-4 text-sm text-gray-700">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={afipForm.billInterest} onChange={(e) => setAfipForm({ ...afipForm, billInterest: e.target.checked })} />
                    Facturar interés
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={afipForm.billPenalty} onChange={(e) => setAfipForm({ ...afipForm, billPenalty: e.target.checked })} />
                    Facturar punitorio
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={afipForm.billPrincipal} onChange={(e) => setAfipForm({ ...afipForm, billPrincipal: e.target.checked })} />
                    Facturar capital
                  </label>
                </div>
                <button type="submit" className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm">Guardar AFIP</button>
              </form>

              {afipConfig && (
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-xs text-gray-500">Último número factura (local): {afipConfig.lastInvoiceNumber}</p>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const synced = await apiService.syncAfipLastInvoiceNumber(afipForm.legalEntityId);
                        setAfipConfig(synced);
                      } catch (e: any) {
                        setError(e?.response?.data?.message ?? e.message);
                      }
                    }}
                    className="px-2 py-1 text-xs border rounded hover:bg-gray-50"
                  >
                    Sincronizar numeración con ARCA
                  </button>
                  <button
                    type="button"
                    disabled={queueBusy}
                    onClick={processInvoiceQueue}
                    className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {queueBusy ? 'Procesando…' : 'Procesar cola ahora'}
                  </button>
                </div>
              )}

              <div className="bg-white border rounded-lg overflow-hidden">
                <div className="px-3 py-2 border-b bg-amber-50 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-amber-900">Cola de facturación ({invoiceQueue.length})</h3>
                  <span className="text-xs text-amber-800">PENDING / REJECTED — se procesan de noche y cada 2 h</span>
                </div>
                {invoiceQueue.length === 0 ? (
                  <p className="p-3 text-sm text-gray-500">Cola vacía.</p>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Cliente</th>
                        <th className="px-3 py-2 text-left">Razón social</th>
                        <th className="px-3 py-2 text-right">Total</th>
                        <th className="px-3 py-2 text-left">Estado</th>
                        <th className="px-3 py-2 text-left">Intentos</th>
                        <th className="px-3 py-2 text-left">Error</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceQueue.map((inv) => (
                        <tr key={inv.id} className="border-t">
                          <td className="px-3 py-2">{inv.customerName}</td>
                          <td className="px-3 py-2 text-xs">{inv.legalEntity?.legalName ?? '—'}</td>
                          <td className="px-3 py-2 text-right">${inv.totalAmount?.toLocaleString()}</td>
                          <td className="px-3 py-2">{inv.status}</td>
                          <td className="px-3 py-2">{inv.attempts ?? 0}</td>
                          <td className="px-3 py-2 text-xs text-red-700 max-w-xs truncate" title={inv.lastError ?? ''}>{inv.lastError ?? '—'}</td>
                          <td className="px-3 py-2">
                            <button type="button" onClick={() => retryInvoice(inv.id)} className="text-blue-600 hover:underline text-xs">Reintentar</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <table className="min-w-full text-sm bg-white border rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Nº</th>
                    <th className="px-3 py-2 text-left">Tipo</th>
                    <th className="px-3 py-2 text-left">Cliente</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-left">CAE</th>
                    <th className="px-3 py-2 text-left">Estado</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {electronicInvoices.map((inv) => (
                    <tr key={inv.id} className="border-t">
                      <td className="px-3 py-2 font-mono">{inv.fullNumber}</td>
                      <td className="px-3 py-2 text-xs">
                        {inv.invoiceType?.replace(/_/g, ' ')}
                        {inv.originalInvoice?.fullNumber && (
                          <span className="block text-gray-500">↳ {inv.originalInvoice.fullNumber}</span>
                        )}
                        {inv.creditNote?.fullNumber && (
                          <span className="block text-gray-500">NC {inv.creditNote.fullNumber}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">{inv.customerName}</td>
                      <td className="px-3 py-2 text-right">${inv.totalAmount?.toLocaleString()}</td>
                      <td className="px-3 py-2 font-mono text-xs">{inv.cae ?? '—'}</td>
                      <td className="px-3 py-2">{inv.status}</td>
                      <td className="px-3 py-2 space-x-2">
                        {inv.status === 'AUTHORIZED' && (
                          <button
                            type="button"
                            onClick={() => downloadInvoicePdf(inv)}
                            className="text-blue-600 hover:underline text-xs"
                          >
                            PDF
                          </button>
                        )}
                        {(inv.status === 'REJECTED' || inv.status === 'PENDING') && (
                          <button onClick={() => retryInvoice(inv.id)} className="text-blue-600 hover:underline text-xs">Reintentar</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {electronicInvoices.length === 0 && (
                <p className="text-gray-500 text-sm">
                  Sin comprobantes. Al cobrar se encola la factura (según flags de la razón social); el job nocturno o «Procesar cola» obtiene el CAE. PDF disponible cuando está AUTHORIZED.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
